import _ from 'lodash';

Mobi2Go._class = Mobi2Go._class || {};

/**
 * Time class
 */
const Time = (Mobi2Go._class.Time = function Time(hours, minutes) {
    this.hours = hours;
    this.minutes = minutes;
});

Time.fromString = function fromString(str) {
    if (!str.match(/^(([0-1][0-9])|(2[0-4])):[0-5][0-9]$/)) {
        throw new Error('invalid time string');
    }

    const groups = str.split(':');
    return new Time(Number(groups[0]), Number(groups[1]));
};

Time.fromDateTime = function fromDateTime(datetime) {
    return new Time(datetime.hours(), datetime.minutes());
};

Time.prototype.clone = function clone() {
    return new Time(this.hours, this.minutes);
};

Time.prototype.round = function round(rounding_minutes) {
    const mod = this.minutes % rounding_minutes;
    if (mod) {
        return this.cloneWithOffset(rounding_minutes - mod);
    }
    return this.clone();
};

Time.prototype.cloneWithOffset = function(offset) {
    let hours_offset = offset / 60;
    hours_offset =
        hours_offset >= 0 ? Math.floor(hours_offset) : Math.ceil(hours_offset);
    const minutes_offset = offset % 60;

    const hours_carry = Math.floor((this.minutes + minutes_offset) / 60);

    const new_minutes = (60 + this.minutes + minutes_offset) % 60;
    const new_hours = (24 + this.hours + hours_offset + hours_carry) % 24;
    return new Time(new_hours, new_minutes);
};

Time.prototype.minutesSinceStartOfDay = function minutesSinceStartOfDay() {
    return this.minutes + this.hours * 60;
};

Time.prototype.isSame = function isSame(time) {
    return this.hours === time.hours && this.minutes === time.minutes;
};

Time.prototype.isAfter = function isAfter(time) {
    if (this.isSame(time)) {
        return false;
    }
    if (this.hours > time.hours) {
        return true;
    }
    return this.hours === time.hours && this.minutes >= time.minutes;
};

Time.prototype.isBefore = function isBefore(time) {
    if (this.isSame(time)) {
        return false;
    }
    return !this.isAfter(time);
};

Time.prototype.isSameOrAfter = function isSameOrAfter(time) {
    return this.isSame(time) || this.isAfter(time);
};

Time.prototype.isSameOrBefore = function isSameOrBefore(time) {
    return this.isSame(time) || this.isBefore(time);
};

Time.prototype.toDateTime = function toDateTime(date) {
    if (!moment.isMoment(date)) {
        date = moment('1970-01-01 00:00:00');
    } else {
        date = date.clone();
    }
    return date
        .hours(this.hours)
        .minutes(this.minutes)
        .second(0);
};

Time.prototype.format = function format(format) {
    format = format || 'HH:mm';
    return this.toDateTime().format(format);
};

Time.prototype.toString = function toString() {
    return this.format();
};

/**
 * TimeRange class
 */
const TimeRange = (Mobi2Go._class.TimeRange = function TimeRange(start, end) {
    this.start = start;
    this.end = end;
});

TimeRange.fromString = function fromString(str) {
    const times = str.split('-');
    if (times.length !== 2) {
        throw new Error('invalid time range string');
    }
    return new TimeRange(Time.fromString(times[0]), Time.fromString(times[1]));
};

TimeRange.fromList = function fromRanges(ranges) {
    if (ranges.length === 0) {
        throw new Error('invalid time range list');
    }
    _.each(ranges, function(range) {
        if (!(range instanceof TimeRange)) {
            throw new Error('invalid time range list');
        }
    });
    let { start } = ranges[0];
    let { end } = ranges[0];
    for (let i = 1; i < ranges.length; i += 1) {
        const range = ranges[i];
        if (range.start.format('Hmm') < start.format('Hmm')) {
            start = range.start;
        }
        if (range.end.format('Hmm') > end.format('Hmm')) {
            end = range.end;
        }
    }
    return new TimeRange(start, end);
};

TimeRange.prototype.cloneWithOffset = function cloneWithOffset(
    start_offset,
    end_offset
) {
    return new TimeRange(
        this.start.cloneWithOffset(start_offset),
        this.end.cloneWithOffset(end_offset)
    );
};

TimeRange.prototype.spillsOverToNextDay = function spillsOverToNextDay() {
    return this.end.isBefore(this.start);
};

TimeRange.prototype.containsTime = function containsTime(time) {
    if (this.spillsOverToNextDay()) {
        return time.isSameOrAfter(this.start) || time.isSameOrBefore(this.end);
    }
    return time.isSameOrAfter(this.start) && time.isSameOrBefore(this.end);
};

TimeRange.prototype.getAvailableTimeSlotsForDate = function getAvailableTimeSlotsForDate(
    date,
    from,
    rounding,
    interval
) {
    const time_slots = [];
    let previous_from;
    rounding = rounding || 5;
    interval = interval || 5;

    from = from || this.start.clone();
    from = from.round(rounding);

    if (this.spillsOverToNextDay()) {
        if (from.isAfter(this.end) && from.isBefore(this.start)) {
            from = this.start.round(rounding);
        }

        let side = from.isSameOrBefore(this.end) ? 'morning' : 'evening';
        const end_of_day = Time.fromString('23:59');

        while (
            (side === 'evening' && from.isSameOrBefore(end_of_day)) ||
            (side === 'morning' && from.isSameOrBefore(this.end))
        ) {
            time_slots.push(from.toDateTime(date));
            previous_from = from;

            if (time_slots.length === 1 && rounding !== interval) {
                from = from.round(interval);
            }

            from = from.cloneWithOffset(interval);

            if (previous_from.isAfter(from)) {
                // switching from evening to morning after passing to the next day
                side = 'morning';
                date = date.clone().add(1, 'day');
            }
        }
        return time_slots;
    }

    if (from.isBefore(this.start)) {
        from = this.start.round(rounding);
    }

    while (from.isSameOrBefore(this.end)) {
        time_slots.push(from.toDateTime(date));
        previous_from = from;

        if (time_slots.length === 1 && rounding !== interval) {
            from = from.round(interval);
        }

        from = from.cloneWithOffset(interval);

        if (previous_from.isAfter(from)) {
            // Special case from + interval > 23:59, we cannot be looping forever
            break;
        }
    }

    return time_slots;
};

TimeRange.prototype.overlaps = function overlaps(time_range) {
    return (
        this.containsTime(time_range.start) || this.containsTime(time_range.end)
    );
};

TimeRange.prototype.format = function format(time_format) {
    return `${this.start.format(time_format)}-${this.end.format(time_format)}`;
};

TimeRange.prototype.toString = function toString() {
    return this.format();
};

/**
 * OperatingHoursForDay
 */
const OperatingHoursForDay = (Mobi2Go._class.OperatingHoursForDay = function OperatingHoursForDay() {
    this.operating_hours = [];
});

OperatingHoursForDay.fromStrings = function fromStrings(strings) {
    const operating_hours_for_day = new OperatingHoursForDay();

    const time_ranges = _.map(strings, function(string) {
        if (string instanceof TimeRange) {
            return string;
        }
        return TimeRange.fromString(string);
    });

    operating_hours_for_day.guardAgainstTimeRangeOverlaps(time_ranges);
    operating_hours_for_day.operating_hours = _.sortBy(time_ranges, function(
        time_range
    ) {
        return time_range.start.minutesSinceStartOfDay();
    });
    return operating_hours_for_day;
};

OperatingHoursForDay.prototype.guardAgainstTimeRangeOverlaps = function guardAgainstTimeRangeOverlaps(
    operating_hours
) {
    for (let i = 0; i < operating_hours.length; i += 1) {
        for (let j = i + 1; j < operating_hours.length; j += 1) {
            if (operating_hours[i].overlaps(operating_hours[j])) {
                throw new Error('overlapping time ranges');
            }
        }
    }
};

OperatingHoursForDay.prototype.spillsOverToNextDay = function spillsOverToNextDay() {
    return _.some(this.operating_hours, function(time_range) {
        return time_range.spillsOverToNextDay();
    });
};

OperatingHoursForDay.prototype.getSpillingTimeRange = function getSpillingTimeRange() {
    return _.find(this.operating_hours, function(time_range) {
        return time_range.spillsOverToNextDay();
    });
};

OperatingHoursForDay.prototype.isOpenAt = function isOpenAt(time) {
    return _.some(this.operating_hours, function(time_range) {
        return time_range.containsTime(time);
    });
};

OperatingHoursForDay.prototype.getTimeRanges = function getTimeRanges() {
    return this.operating_hours;
};

OperatingHoursForDay.prototype.count = function count() {
    return this.operating_hours.length;
};

OperatingHoursForDay.prototype.toString = function toString() {
    return _.map(this.operating_hours, function(time_range) {
        return time_range.toString();
    }).join(',');
};

OperatingHoursForDay.prototype.serialize = function serialize() {
    return _.map(this.operating_hours, function(time_range) {
        return time_range.format();
    });
};

/**
 * Day class
 */
const Day = (Mobi2Go._class.Day = {});

Day.MONDAY = 'monday';
Day.TUESDAY = 'tuesday';
Day.WEDNESDAY = 'wednesday';
Day.THURSDAY = 'thursday';
Day.FRIDAY = 'friday';
Day.SATURDAY = 'saturday';
Day.SUNDAY = 'sunday';

Day.days = function days() {
    return [
        Day.MONDAY,
        Day.TUESDAY,
        Day.WEDNESDAY,
        Day.THURSDAY,
        Day.FRIDAY,
        Day.SATURDAY,
        Day.SUNDAY,
    ];
};

Day.mapDays = function mapDays(callback) {
    return _.mapValues(_.zipObject(Day.days(), Day.days()), callback);
};

Day.normalizeDayName = function normalizeDayName(day) {
    day = day.toLowerCase();
    if (!Day.isValid(day)) {
        throw new Error('invalid day name');
    }
    return day;
};

Day.isValid = function isValid(day) {
    return _.includes(Day.days(), day);
};

Day.onDateTime = function onDateTime(date) {
    return Day.days()[(date.day() - 1 + 7) % 7];
};

/**
 * OperatingHours class
 */
const OperatingHours = (Mobi2Go._class.OperatingHours = function OperatingHours(
    utc_offset
) {
    this.setUtcOffset(utc_offset);
    this.overrides = [];
    this.operating_hours = Day.mapDays(function() {
        return new OperatingHoursForDay();
    });
});

OperatingHours.create = function create(data, utc_offset) {
    return new OperatingHours(utc_offset).fill(data);
};

OperatingHours.createAndMergeOverlappingRanges = function createAndMergeOverlappingRanges(
    data,
    utc_offset
) {
    return OperatingHours.create(
        OperatingHours.mergeOverlappingRanges(data),
        utc_offset
    );
};

OperatingHours.mergeOverlappingRanges = function mergeOverlappingRanges(data) {
    const result = [];
    let ranges = [];

    _.each(_.keys(data), function(key) {
        let value = data[key];

        value = _.isArray(value)
            ? OperatingHours.mergeOverlappingRanges(value)
            : _.isString(value)
            ? TimeRange.fromString(value)
            : value;

        if (value instanceof TimeRange) {
            const new_ranges = [];
            let skip = false;
            _.each(ranges, function(range) {
                if (skip) {
                    return;
                }

                if (value.format() === range.format()) {
                    skip = true;
                    return;
                }

                if (value.overlaps(range) || range.overlaps(value)) {
                    value = TimeRange.fromList([value, range]);

                    return;
                }

                new_ranges.push(range);
            });

            if (skip) {
                return;
            }

            new_ranges.push(value);
            ranges = new_ranges;

            return;
        }

        result[key] = value;
    });

    _.each(_.keys(ranges), function(key) {
        result.push(ranges[key]);
    });
    return result;
};

OperatingHours.isValid = function isValid(data) {
    try {
        OperatingHours.create(data);
        return true;
    } catch (e) {
        return false;
    }
};

OperatingHours.prototype.setUtcOffset = function setUtcOffset(utc_offset) {
    this.utc_offset = utc_offset;
};

OperatingHours.prototype.cloneWithOffset = function cloneWithOffset(
    start_offset,
    end_offset
) {
    const that = this;
    // Clone regular days with offset
    let carry_to_next_day = [];
    const data = [];
    _.each(Day.days(), function(day) {
        data[day] = carry_to_next_day;
        carry_to_next_day = [];

        _.each(that.operating_hours[day].operating_hours, function(time_range) {
            const offset_time_range = time_range.cloneWithOffset(
                start_offset,
                end_offset
            );

            if (
                start_offset > 0 &&
                offset_time_range.start.isBefore(time_range.start)
            ) {
                // The offset pushed the time range onto the next day
                carry_to_next_day.push(offset_time_range);
            } else {
                data[day].push(offset_time_range);
            }
        });
    });
    if (carry_to_next_day.length > 0) {
        data[Day.days()[0]] = data[Day.days()[0]].concat(carry_to_next_day);
    }

    // Clone overrides with offset
    data.overrides = [];
    _.each(_.keys(this.overrides), function(date) {
        const exception_for_date = that.overrides[date];

        data.overrides[date] = [];
        _.each(exception_for_date.operating_hours, function(time_range) {
            const offset_time_range = time_range.cloneWithOffset(
                start_offset,
                end_offset
            );

            if (
                start_offset > 0 &&
                offset_time_range.start.isBefore(time_range.start)
            ) {
                let format = 'MM-DD';
                let datetime = moment(date, format);
                if (!datetime.isValid()) {
                    format = 'YYYY-MM-DD';
                    datetime = moment(date, format);
                }

                datetime.add(1, 'day');
                const date_key = datetime.format(format);
                data.overrides[date_key] = data.overrides[date_key] || [];
                data.overrides[date_key].push(offset_time_range);
            } else {
                data.overrides[date].push(offset_time_range);
            }
        });
    });

    return OperatingHours.createAndMergeOverlappingRanges(
        data,
        this.utc_offset
    );
};

OperatingHours.prototype.fill = function fill(data) {
    const overrides = this.parseOverrides(data);
    const operating_hours = this.parseOperatingHours(data);
    const that = this;
    _.each(_.keys(operating_hours), function(day) {
        that.operating_hours[day] = OperatingHoursForDay.fromStrings(
            operating_hours[day]
        );
    });

    this.overrides = _.mapValues(overrides, function(operating_hours, date) {
        const recurring = moment(date, 'MM-DD').isValid();
        if (!recurring) {
            const date_time = moment(date, 'YYYY-MM-DD').isValid();

            if (!date_time) {
                throw new Error('invalid date');
            }
        }
        return OperatingHoursForDay.fromStrings(operating_hours);
    });
    return this;
};

OperatingHours.prototype.parseOverrides = function parseOverrides(data) {
    return 'overrides' in data ? data.overrides : [];
};

OperatingHours.prototype.parseOperatingHours = function parseOperatingHours(
    data
) {
    const operating_hours = [];

    _.each(_.keys(data), function(day) {
        if (Day.isValid(day.toLowerCase())) {
            operating_hours[Day.normalizeDayName(day)] = data[day];
        }
    });
    return operating_hours;
};

OperatingHours.prototype.forDay = function forDay(day) {
    day = Day.normalizeDayName(day);
    return this.operating_hours[day];
};

OperatingHours.prototype.forDate = function forDate(date) {
    date = this.applyUtcOffset(date);
    let exception_date = date.format('YYYY-MM-DD');
    if (exception_date in this.overrides) {
        return this.overrides[exception_date];
    }
    exception_date = date.format('MM-DD');
    if (exception_date in this.overrides) {
        return this.overrides[exception_date];
    }

    return this.forDay(Day.onDateTime(date));
};

OperatingHours.prototype.isOpenOn = function isOpenOn(day) {
    return this.forDay(day).count() > 0;
};

OperatingHours.prototype.isClosedOn = function isClosedOn(day) {
    return !this.isOpenOn(day);
};

OperatingHours.prototype.isOpenAt = function isOpenAt(date_time) {
    date_time = this.applyUtcOffset(date_time);

    let operating_hours_for_day = this.forDate(date_time);
    const time = Time.fromDateTime(date_time);
    let is_open_at;

    if (
        operating_hours_for_day.spillsOverToNextDay() &&
        time.isBefore(operating_hours_for_day.getTimeRanges()[0].start)
    ) {
        // If the day spills to the next and the time to check is before the start of the day
        // then it's not contained by the day opening hours
        // i.e Tuesday open 10AM to 2AM.
        // Is it opened on Tuesday at 1AM? No, tuesday opening hours start at 10AM
        is_open_at = false;
    } else {
        is_open_at = operating_hours_for_day.isOpenAt(time);
    }

    if (!is_open_at) {
        // If the day before the given date has a time range that spills over next day
        // Check if it's open on that date past midnight
        date_time = date_time.clone().subtract(1, 'day');

        operating_hours_for_day = this.forDate(date_time);
        if (operating_hours_for_day.spillsOverToNextDay()) {
            const spilling_time_range = operating_hours_for_day.getSpillingTimeRange();
            is_open_at = time.isSameOrBefore(spilling_time_range.end);
        }
    }

    return is_open_at;
};

OperatingHours.prototype.isClosedAt = function isClosedAt(date_time) {
    return !this.isOpenAt(date_time);
};

OperatingHours.prototype.isOpen = function isOpen() {
    return this.isOpenAt(moment());
};

OperatingHours.prototype.isClosed = function isClosed() {
    return !this.isOpen();
};

OperatingHours.prototype.applyUtcOffset = function applyUtcOffset(date) {
    if (this.utc_offset !== undefined) {
        return date.clone().utcOffset(this.utc_offset);
    }
    return date.clone();
};

OperatingHours.prototype.serialize = function serialize() {
    const that = this;
    return Day.mapDays(function(day) {
        return that.operating_hours[day].serialize();
    });
};

OperatingHours.prototype.getAvailableTimeSlotsForDate = function getAvailableTimeSlotsForDate(
    date,
    from,
    rounding,
    interval
) {
    let time_slots = [];
    const operating_hours_for_date = this.forDate(date);

    from = this.applyUtcOffset(from);

    if (from.isSame(date, 'day')) {
        from = Time.fromDateTime(from);
    } else {
        // from is not the same day as the date
        // we'll need to return all the available time slots for the date
        from = null;
    }

    // Get yesterday slots that are spilling over today
    const operating_hours_for_yesterday = this.forDate(
        date.clone().subtract(1, 'day')
    );
    if (operating_hours_for_yesterday.spillsOverToNextDay()) {
        const spilling_time_range = operating_hours_for_yesterday.getSpillingTimeRange();
        if (from === null || from.isSameOrBefore(spilling_time_range.end)) {
            time_slots = time_slots.concat(
                spilling_time_range.getAvailableTimeSlotsForDate(
                    date,
                    from || Time.fromString('00:00'),
                    rounding,
                    interval
                )
            );
        }
    }

    // Get today's slots
    const ordered_time_ranges = operating_hours_for_date.getTimeRanges();
    _.each(ordered_time_ranges, function(time_range) {
        time_slots = time_slots.concat(
            time_range.getAvailableTimeSlotsForDate(
                date,
                from,
                rounding,
                interval
            )
        );
    });

    return time_slots;
};

export { Time };
